import React, { useState, useEffect } from 'react';
import {
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { BilingualAssignmentForm } from '../../components/forms/BilingualAssignmentForm';
import { Vehicle, Driver } from '../../types';
import { useVehicleStore } from '../../stores/vehicleStore';
import { useDriverStore } from '../../stores/driverStore';
import { useAssignmentFormStore } from '../../stores/assignmentFormStore';
import theme from '../../theme';

interface VehicleSwapScreenProps {
  navigation: any;
  route: {
    params?: {
      currentVehicleId?: string;
      newVehicleId?: string;
    };
  };
}

type SwapStep = 'delivery' | 'receiving' | 'complete';

export const VehicleSwapScreen: React.FC<VehicleSwapScreenProps> = ({
  navigation,
  route,
}) => {
  const [currentStep, setCurrentStep] = useState<SwapStep>('delivery');
  const [currentVehicle, setCurrentVehicle] = useState<Vehicle | null>(null);
  const [newVehicle, setNewVehicle] = useState<Vehicle | null>(null);
  const [driver, setDriver] = useState<Driver | null>(null);
  const [deliveryFormData, setDeliveryFormData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const vehicles = useVehicleStore((state) => state.vehicles);
  const drivers = useDriverStore((state) => state.drivers);
  const submitDeliveryForm = useAssignmentFormStore((state) => state.submitDeliveryForm);
  const submitReceivingForm = useAssignmentFormStore((state) => state.submitReceivingForm);
  const submitAssignmentForm = useAssignmentFormStore((state) => state.submitAssignmentForm);

  useEffect(() => {
    // Get vehicles from route params or require selection
    const { currentVehicleId, newVehicleId } = route.params || {};
    
    if (currentVehicleId) {
      const currentVeh = vehicles.find(v => v.id === currentVehicleId);
      if (currentVeh) {
        setCurrentVehicle(currentVeh);
        
        // Auto-select the driver assigned to the current vehicle
        if (currentVeh.currentDriverId) {
          const assignedDriver = drivers.find(d => d.id === currentVeh.currentDriverId);
          if (assignedDriver) {
            setDriver(assignedDriver);
          }
        }
      }
    }

    if (newVehicleId) {
      const newVeh = vehicles.find(v => v.id === newVehicleId);
      if (newVeh) {
        setNewVehicle(newVeh);
      }
    }

    // If vehicles not provided, go back to selection
    if (!currentVehicleId || !newVehicleId) {
      Alert.alert(
        'خطأ / Error',
        'لم يتم تحديد المركبات للتبديل / Vehicles not specified for swap',
        [{ text: 'موافق / OK', onPress: () => navigation.goBack() }]
      );
    }
  }, [route.params, vehicles, drivers]);

  const handleDeliverySubmit = async (formData: any) => {
    if (!currentVehicle || !driver) {
      Alert.alert('خطأ / Error', 'بيانات المركبة أو السائق مفقودة / Vehicle or driver data missing');
      return;
    }

    setLoading(true);
    try {
      console.log('🚚 Submitting delivery form for vehicle swap...');
      
      // Ensure timestamp is current for delivery
      const deliveryFormWithTimestamp = {
        ...formData,
        timestamp: new Date().toISOString(),
      };
      
      // Submit delivery form for current vehicle (being returned)
      // Pass isSwap flag to skip validation since vehicle is already assigned
      await submitDeliveryForm(deliveryFormWithTimestamp, currentVehicle.id, driver.id, undefined, true);
      
      // Store delivery form data for reference
      setDeliveryFormData(deliveryFormWithTimestamp);
      
      // Move to receiving step
      setCurrentStep('receiving');
      
      Alert.alert(
        'تم التسليم / Delivery Complete',
        `تم تسليم المركبة ${currentVehicle.licensePlate} بنجاح\nالآن يمكنك استلام المركبة الجديدة\n\nVehicle ${currentVehicle.licensePlate} delivered successfully\nNow proceed to receive the new vehicle`,
        [{ text: 'التالي / Next' }]
      );
    } catch (error: any) {
      console.error('❌ Error submitting delivery form:', error);
      Alert.alert(
        'خطأ / Error',
        `فشل في تسليم المركبة: ${error.message}\nDelivery failed: ${error.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReceivingSubmit = async (formData: any) => {
    if (!newVehicle || !driver) {
      Alert.alert('خطأ / Error', 'بيانات المركبة أو السائق مفقودة / Vehicle or driver data missing');
      return;
    }

    setLoading(true);
    try {
      console.log('📥 Submitting receiving form for vehicle swap...');
      
      // CRITICAL FIX: For swap, we need to create an assignment first before receiving
      // Step 1: Create assignment for the new vehicle
      console.log('🔄 Creating assignment for new vehicle in swap...');
      const assignmentData = {
        vehicleId: newVehicle.id,
        driverId: driver.id,
        assignmentDate: new Date().toISOString().split('T')[0], // Today's date
        isSharedAssignment: false,
        secondaryDriverId: undefined,
      };
      
      await submitAssignmentForm(assignmentData);
      console.log('✅ Assignment created for new vehicle');
      
      // Step 2: Now submit receiving form for the newly assigned vehicle
      // Ensure receiving timestamp is after delivery timestamp
      const deliveryTime = deliveryFormData?.timestamp ? new Date(deliveryFormData.timestamp) : new Date();
      const receivingTime = new Date(Math.max(deliveryTime.getTime() + 1000, Date.now())); // At least 1 second after delivery
      
      const receivingFormWithTimestamp = {
        ...formData,
        timestamp: receivingTime.toISOString(),
      };
      
      // Submit receiving form for new vehicle (now has active assignment)
      await submitReceivingForm(receivingFormWithTimestamp, newVehicle.id, driver.id);
      
      setCurrentStep('complete');
      
      Alert.alert(
        'تم التبديل / Swap Complete',
        `تم تبديل المركبة بنجاح!\n\nالمركبة القديمة: ${currentVehicle?.licensePlate} - تم التسليم\nالمركبة الجديدة: ${newVehicle.licensePlate} - تم الاستلام\n\nVehicle swap completed successfully!\n\nOld vehicle: ${currentVehicle?.licensePlate} - Delivered\nNew vehicle: ${newVehicle.licensePlate} - Received`,
        [
          {
            text: 'العودة للقائمة / Back to List',
            onPress: () => {
              // Navigate back to assignments main screen
              navigation.navigate('AssignmentMain');
            }
          }
        ]
      );
    } catch (error: any) {
      console.error('❌ Error submitting receiving form:', error);
      Alert.alert(
        'خطأ / Error',
        `فشل في استلام المركبة: ${error.message}\nReceiving failed: ${error.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    Alert.alert(
      'إلغاء التبديل / Cancel Swap',
      'هل أنت متأكد من إلغاء عملية تبديل المركبة؟\nAre you sure you want to cancel the vehicle swap?',
      [
        { text: 'لا / No', style: 'cancel' },
        {
          text: 'نعم / Yes',
          style: 'destructive',
          onPress: () => navigation.goBack()
        }
      ]
    );
  };

  const renderStepIndicator = () => (
    <Card style={styles.stepIndicator}>
      <Text style={styles.stepTitle}>خطوات تبديل المركبة / Vehicle Swap Steps</Text>
      <View style={styles.stepContainer}>
        <View style={styles.stepItem}>
          <View style={[
            styles.stepCircle,
            currentStep === 'delivery' ? styles.activeStep : 
            currentStep === 'receiving' || currentStep === 'complete' ? styles.completedStep : styles.inactiveStep
          ]}>
            <Text style={[
              styles.stepNumber,
              currentStep === 'delivery' ? styles.activeStepText : styles.inactiveStepText
            ]}>1</Text>
          </View>
          <Text style={styles.stepLabel}>تسليم المركبة الحالية{'\n'}Deliver Current Vehicle</Text>
        </View>
        
        <View style={styles.stepConnector} />
        
        <View style={styles.stepItem}>
          <View style={[
            styles.stepCircle,
            currentStep === 'receiving' ? styles.activeStep :
            currentStep === 'complete' ? styles.completedStep : styles.inactiveStep
          ]}>
            <Text style={[
              styles.stepNumber,
              currentStep === 'receiving' ? styles.activeStepText : styles.inactiveStepText
            ]}>2</Text>
          </View>
          <Text style={styles.stepLabel}>استلام المركبة الجديدة{'\n'}Receive New Vehicle</Text>
        </View>
      </View>
    </Card>
  );

  const renderVehicleInfo = () => (
    <Card style={styles.vehicleInfo}>
      <Text style={styles.vehicleInfoTitle}>معلومات المركبات / Vehicle Information</Text>
      <View style={styles.vehicleRow}>
        <View style={styles.vehicleItem}>
          <Text style={styles.vehicleLabel}>المركبة الحالية / Current Vehicle:</Text>
          <Text style={styles.vehicleValue}>
            {currentVehicle?.licensePlate} - {currentVehicle?.makeModel}
          </Text>
        </View>
        <Ionicons name="arrow-forward" size={24} color={theme.colors.primary} />
        <View style={styles.vehicleItem}>
          <Text style={styles.vehicleLabel}>المركبة الجديدة / New Vehicle:</Text>
          <Text style={styles.vehicleValue}>
            {newVehicle?.licensePlate} - {newVehicle?.makeModel}
          </Text>
        </View>
      </View>
      {driver && (
        <View style={styles.driverInfo}>
          <Text style={styles.driverLabel}>السائق / Driver:</Text>
          <Text style={styles.driverValue}>{driver.name} - {driver.phone}</Text>
        </View>
      )}
    </Card>
  );

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>جاري المعالجة... / Processing...</Text>
      </View>
    );
  }

  if (currentStep === 'complete') {
    return (
      <View style={styles.container}>
        <View style={styles.completeContainer}>
          <Ionicons name="checkmark-circle" size={100} color={theme.colors.success} />
          <Text style={styles.completeTitle}>تم تبديل المركبة بنجاح!</Text>
          <Text style={styles.completeSubtitle}>Vehicle Swap Completed Successfully!</Text>
          <Button
            title="العودة للقائمة / Back to List"
            onPress={() => navigation.navigate('AssignmentMain')}
            variant="primary"
            style={styles.completeButton}
          />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={handleCancel}>
          <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
        </TouchableOpacity>
        <View style={styles.headerContent}>
          <Text style={styles.headerTitle}>تبديل المركبة / Vehicle Swap</Text>
          <Text style={styles.headerSubtitle}>
            {currentStep === 'delivery' 
              ? 'الخطوة 1: تسليم المركبة الحالية / Step 1: Deliver Current Vehicle'
              : 'الخطوة 2: استلام المركبة الجديدة / Step 2: Receive New Vehicle'
            }
          </Text>
        </View>
      </View>

      <ScrollView 
        style={styles.content}
        showsVerticalScrollIndicator={true}
        bounces={true}
        contentContainerStyle={styles.scrollContent}
      >
        {renderStepIndicator()}
        {renderVehicleInfo()}

        {currentStep === 'delivery' && currentVehicle && driver && (
          <BilingualAssignmentForm
            formType="delivery"
            vehicle={currentVehicle}
            driver={driver}
            onSubmit={handleDeliverySubmit}
            onCancel={handleCancel}
            onPrint={() => {
              // Handle print functionality if needed
              console.log('Print delivery form');
            }}
          />
        )}

        {currentStep === 'receiving' && newVehicle && driver && (
          <BilingualAssignmentForm
            formType="receiving"
            vehicle={newVehicle}
            driver={driver}
            onSubmit={handleReceivingSubmit}
            onCancel={handleCancel}
            onPrint={() => {
              // Handle print functionality if needed
              console.log('Print receiving form');
            }}
          />
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
  },
  headerSubtitle: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginTop: 2,
  },
  content: {
    flex: 1,
    padding: 16,
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 20,
  },
  stepIndicator: {
    marginBottom: 16,
  },
  stepTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
    textAlign: 'center',
    marginBottom: 16,
  },
  stepContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepItem: {
    alignItems: 'center',
    flex: 1,
  },
  stepCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  activeStep: {
    backgroundColor: theme.colors.primary,
  },
  completedStep: {
    backgroundColor: theme.colors.success,
  },
  inactiveStep: {
    backgroundColor: theme.colors.border,
  },
  stepNumber: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  activeStepText: {
    color: theme.colors.surface,
  },
  inactiveStepText: {
    color: theme.colors.textSecondary,
  },
  stepLabel: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    lineHeight: 16,
  },
  stepConnector: {
    width: 40,
    height: 2,
    backgroundColor: theme.colors.border,
    marginHorizontal: 8,
  },
  vehicleInfo: {
    marginBottom: 16,
  },
  vehicleInfoTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: theme.colors.textPrimary,
    marginBottom: 12,
    textAlign: 'center',
  },
  vehicleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  vehicleItem: {
    flex: 1,
  },
  vehicleLabel: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    marginBottom: 4,
  },
  vehicleValue: {
    fontSize: 14,
    color: theme.colors.textPrimary,
    fontWeight: '500',
  },
  driverInfo: {
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
  },
  driverLabel: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    marginBottom: 4,
  },
  driverValue: {
    fontSize: 14,
    color: theme.colors.textPrimary,
    fontWeight: '500',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: theme.colors.textSecondary,
  },
  completeContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  completeTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.success,
    textAlign: 'center',
    marginTop: 24,
  },
  completeSubtitle: {
    fontSize: 18,
    color: theme.colors.textPrimary,
    textAlign: 'center',
    marginTop: 8,
    marginBottom: 32,
  },
  completeButton: {
    minWidth: 200,
  },
});